import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import { CRM_CONTACT_TYPES } from '@bcc/shared';
import type { CrmContactWithMeta } from '@bcc/shared';
import { AddContactModal } from '../../components/crm/AddContactModal';
import { CrmNotesRailTab } from '../../components/crm/CrmNotesRailTab';
import { EditContactModal } from '../../components/crm/EditContactModal';
import { SITE_FORM_CONTROL, SITE_GLASS_CARD, SITE_SELECT_OPTIONS } from '../../constants/siteUi';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatPhoneForDisplay } from '../../lib/phoneIntl';

function notesRailLayoutEqual(
  a: { topSpacerPx: number; rowPx: Record<string, number> },
  b: { topSpacerPx: number; rowPx: Record<string, number> },
): boolean {
  if (a.topSpacerPx !== b.topSpacerPx) return false;
  const keysA = Object.keys(a.rowPx);
  const keysB = Object.keys(b.rowPx);
  if (keysA.length !== keysB.length) return false;
  for (const k of keysA) {
    if (a.rowPx[k] !== b.rowPx[k]) return false;
  }
  return true;
}

function accreditedCell(v: boolean | null | undefined): string {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  return '—';
}

function IconPencil({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
      />
    </svg>
  );
}

function IconTrash({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c-.34-.059-.68-.114-1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
      />
    </svg>
  );
}

const editContactRowBtn =
  'inline-flex items-center gap-1 rounded-lg border border-white/14 bg-white/[0.06] px-2 py-1.5 font-display text-[0.55rem] font-bold uppercase tracking-[0.1em] text-[var(--home-text-on-dark)] ring-1 ring-white/[0.05] transition hover:border-[var(--home-miami-blue)]/35 hover:bg-white/[0.1]';

const deleteContactRowBtn =
  'inline-flex items-center gap-1 rounded-lg border border-white/14 bg-white/[0.06] px-2 py-1.5 font-display text-[0.55rem] font-bold uppercase tracking-[0.1em] text-rose-200/95 ring-1 ring-white/[0.05] transition hover:border-rose-400/45 hover:bg-rose-500/[0.14]';

/** Filterable CRM directory — capital partners, intermediaries, principals. */

export function CrmContacts() {
  const { session } = useAuth();
  const token = session?.access_token;
  const [contacts, setContacts] = useState<CrmContactWithMeta[]>([]);
  const [type, setType] = useState<string>('all');
  const [status, setStatus] = useState<string>('all');
  const [q, setQ] = useState('');
  const [modal, setModal] = useState(false);
  const [editContact, setEditContact] = useState<CrmContactWithMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [listNonce, setListNonce] = useState(0);

  const theadRef = useRef<HTMLTableSectionElement>(null);
  const contactsGlassCardRef = useRef<HTMLDivElement>(null);
  const contactRowEls = useRef(new Map<string, HTMLTableRowElement>());
  const notesRailRowEls = useRef(new Map<string, HTMLDivElement>());
  const [notesOpenContactId, setNotesOpenContactId] = useState<string | null>(null);
  const [notesRailExpandedContactId, setNotesRailExpandedContactId] = useState<string | null>(null);
  const [notesRailLayout, setNotesRailLayout] = useState<{ topSpacerPx: number; rowPx: Record<string, number> }>({
    topSpacerPx: 0,
    rowPx: {},
  });

  const debouncedSearch = useDebounce(q, 300);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const params = new URLSearchParams();
      if (type !== 'all') params.set('type', type);
      if (status !== 'all') params.set('status', status);
      if (debouncedSearch.trim()) params.set('q', debouncedSearch.trim());
      const qs = params.toString();
      const res = await apiFetch<{ contacts: CrmContactWithMeta[] }>(
        `/api/crm/contacts${qs ? `?${qs}` : ''}`,
        { token },
      );
      if (!cancelled) {
        setContacts(res.contacts);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, type, status, debouncedSearch, listNonce]);

  const measureNotesRailLayout = useCallback(() => {
    /** Contacts filters live *above* the table card; Admin Leads keeps filters inside the card. Only thead height offsets tbody rows within this card. */
    const theadEl = theadRef.current;
    const topSpacerPx = theadEl ? Math.round(theadEl.getBoundingClientRect().height) : 0;
    const rowPx: Record<string, number> = {};
    for (const c of contacts) {
      const el = contactRowEls.current.get(c.id);
      if (el) rowPx[c.id] = Math.round(el.getBoundingClientRect().height);
    }
    setNotesRailLayout((prev) => {
      const next = { topSpacerPx, rowPx };
      return notesRailLayoutEqual(prev, next) ? prev : next;
    });
  }, [contacts]);

  useLayoutEffect(() => {
    measureNotesRailLayout();
    const ro = new ResizeObserver(() => measureNotesRailLayout());
    if (theadRef.current) ro.observe(theadRef.current);
    for (const c of contacts) {
      const el = contactRowEls.current.get(c.id);
      if (el) ro.observe(el);
    }
    window.addEventListener('resize', measureNotesRailLayout);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measureNotesRailLayout);
    };
  }, [contacts, measureNotesRailLayout]);

  useEffect(() => {
    setNotesRailExpandedContactId((id) => (id != null && contacts.some((x) => x.id === id) ? id : null));
    setNotesOpenContactId((id) => (id != null && contacts.some((x) => x.id === id) ? id : null));
  }, [contacts]);

  const deleteStaffContact = useCallback(
    async (c: CrmContactWithMeta) => {
      if (!token) return;
      const label = c.full_name?.trim() || c.email || 'this contact';
      if (!window.confirm(`Permanently delete ${label}? This cannot be undone.`)) return;
      try {
        await apiFetch(`/api/crm/contacts/${encodeURIComponent(c.id)}`, { method: 'DELETE', token });
        setEditContact((cur) => (cur?.id === c.id ? null : cur));
        setListNonce((n) => n + 1);
      } catch {
        window.alert('Could not delete contact.');
      }
    },
    [token],
  );

  const subtitle = useMemo(
    () => `${contacts.length} record${contacts.length === 1 ? '' : 's'} · filters apply instantly`,
    [contacts.length],
  );

  const showNotesRail = Boolean(token && contacts.length > 0);
  const tableColSpan = 12;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-[var(--home-text-on-dark)]">CRM contacts</h1>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-[var(--home-muted-on-dark)]">{subtitle}</p>
        </div>
        <button
          type="button"
          onClick={() => setModal(true)}
          className="rounded-xl bg-[var(--home-miami-blue)] px-5 py-2.5 text-sm font-semibold text-[var(--home-zone-void)] transition hover:brightness-105"
        >
          Add contact
        </button>
      </div>

      <div className={`flex flex-wrap items-center gap-4 p-4 ${SITE_GLASS_CARD}`}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name / email / phone / commitment"
          className={`min-w-[220px] flex-1 ${SITE_FORM_CONTROL} px-4`}
        />
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className={`${SITE_FORM_CONTROL} ${SITE_SELECT_OPTIONS} capitalize`}
        >
          <option value="all">All types</option>
          {Object.values(CRM_CONTACT_TYPES).map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className={`${SITE_FORM_CONTROL} ${SITE_SELECT_OPTIONS} capitalize`}
        >
          <option value="all">All statuses</option>
          <option value="new">New</option>
          <option value="contacted">Contacted</option>
          <option value="engaged">Engaged</option>
          <option value="qualified">Qualified</option>
          <option value="converted">Converted</option>
          <option value="lost">Lost</option>
        </select>
      </div>

      <div className="relative flex min-w-0 items-start gap-0">
        <div ref={contactsGlassCardRef} className={`min-w-0 flex-1 overflow-hidden ${SITE_GLASS_CARD}`}>
          <div className="min-w-0 overflow-x-auto">
            <table className="w-full min-w-[1240px] text-sm">
              <thead
                ref={theadRef}
                className="bg-white/[0.06] text-left text-[11px] uppercase tracking-wide text-[var(--home-muted-on-dark)]"
              >
                <tr>
                  <th className="px-4 py-3">Updated</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="min-w-[8rem] px-4 py-3">Commitment</th>
                  <th className="px-4 py-3">Accredited</th>
                  <th className="px-4 py-3">Channel</th>
                  <th className="px-4 py-3">Pipeline</th>
                  <th className="px-4 py-3">Tags</th>
                  <th className="px-4 py-3">Assignee</th>
                  <th className="whitespace-nowrap px-4 py-3">Actions</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={tableColSpan} className="px-5 py-8 text-center text-[var(--home-muted-on-dark)]">
                      Hydrating relational graph…
                    </td>
                  </tr>
                ) : (
                  contacts.map((c) => (
                    <tr
                      key={c.id}
                      ref={(el) => {
                        if (el) contactRowEls.current.set(c.id, el);
                        else contactRowEls.current.delete(c.id);
                      }}
                      id={`crm-contact-${c.id}`}
                      className="border-t border-white/[0.06] align-top transition-colors hover:bg-white/[0.04]"
                    >
                      <td className="px-4 py-3 text-xs text-[var(--home-muted-on-dark)]">
                        {new Date(c.updated_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 font-medium text-[var(--home-text-on-dark)]">{c.full_name}</td>
                      <td className="max-w-[11rem] break-all px-4 py-3 text-xs">{c.email ?? '—'}</td>
                      <td className="px-4 py-3 text-xs">{formatPhoneForDisplay(c.phone) || '—'}</td>
                      <td className="max-w-[10rem] px-4 py-3 text-xs text-[var(--home-muted-on-dark)]">
                        {c.desired_commitment?.trim() ? c.desired_commitment : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs">{accreditedCell(c.accredited_investor)}</td>
                      <td className="px-4 py-3 capitalize text-emerald-200/90">{c.type}</td>
                      <td className="px-4 py-3 capitalize text-blue-200/90">{c.status}</td>
                      <td className="px-4 py-3 text-xs font-mono text-slate-500">
                        {(c.tags ?? []).slice(0, 3).join(', ') || '—'}
                      </td>
                      <td className="px-4 py-3 text-xs">{c.assigned_name ?? '—'}</td>
                      <td className="whitespace-nowrap px-4 py-3">
                        {token ? (
                          <div className="flex flex-wrap items-center gap-1.5">
                            <button
                              type="button"
                              className={editContactRowBtn}
                              title="Edit contact"
                              onClick={() => setEditContact(c)}
                            >
                              <IconPencil className="h-3.5 w-3.5 text-[var(--home-miami-blue)]" aria-hidden />
                              Edit
                            </button>
                            <button
                              type="button"
                              className={deleteContactRowBtn}
                              title="Delete contact"
                              onClick={() => void deleteStaffContact(c)}
                            >
                              <IconTrash className="h-3.5 w-3.5 text-rose-300/90" aria-hidden />
                              Delete
                            </button>
                          </div>
                        ) : (
                          <span className="text-[var(--home-muted-on-dark)]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/admin/crm/contacts/${c.id}`}
                          className="text-xs font-medium text-[var(--home-miami-blue)] hover:brightness-110"
                        >
                          Open dossier →
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
                {!loading && contacts.length === 0 ? (
                  <tr>
                    <td colSpan={tableColSpan} className="px-5 py-10 text-center text-[var(--home-muted-on-dark)]">
                      Zero matches — widen filters or add a counterpart.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>

        {showNotesRail && token ? (
          <div className="flex w-10 shrink-0 flex-col overflow-visible">
            <div
              className="shrink-0"
              style={
                notesRailLayout.topSpacerPx > 0 ? { height: notesRailLayout.topSpacerPx } : { minHeight: '4rem' }
              }
            />
            {contacts.map((c, idx) => {
              const h = notesRailLayout.rowPx[c.id] ?? 0;
              const tabVariant = idx % 2 === 0 ? 'pink' : 'blue';
              return (
                <div
                  key={`crm-notes-rail-${c.id}`}
                  ref={(el) => {
                    if (el) notesRailRowEls.current.set(c.id, el);
                    else notesRailRowEls.current.delete(c.id);
                  }}
                  className={`relative flex shrink-0 items-stretch overflow-visible ${notesRailExpandedContactId === c.id ? 'z-[100]' : 'z-0'}`}
                  style={h > 0 ? { height: h } : { minHeight: '2.75rem' }}
                >
                  <CrmNotesRailTab
                    recordId={c.id}
                    label={c.full_name ?? c.email ?? 'Contact'}
                    notes={c.notes ?? null}
                    token={token}
                    patchUrl={`/api/crm/contacts/${c.id}`}
                    notesBodyKey="notes"
                    placeholder="CRM notes…"
                    ariaRegionLabel="CRM notes"
                    tabVariant={tabVariant}
                    railRowId={c.id}
                    containerRef={contactsGlassCardRef}
                    notesRailRowElsRef={notesRailRowEls}
                    notesOpen={notesOpenContactId === c.id}
                    onNotesOpenChange={(next) => {
                      if (next) {
                        setNotesOpenContactId(c.id);
                        setNotesRailExpandedContactId(null);
                      } else {
                        setNotesOpenContactId((id) => (id === c.id ? null : id));
                        setNotesRailExpandedContactId((eid) => (eid === c.id ? null : eid));
                      }
                    }}
                    heightExpanded={notesRailExpandedContactId === c.id}
                    onHeightExpandedChange={(next) => setNotesRailExpandedContactId(next ? c.id : null)}
                    onSaved={(id, notes) =>
                      setContacts((prev) => prev.map((x) => (x.id === id ? { ...x, notes } : x)))
                    }
                  />
                </div>
              );
            })}
          </div>
        ) : null}
      </div>

      <AddContactModal
        token={token ?? ''}
        open={modal}
        onClose={() => setModal(false)}
        onCreated={(contact) => {
          setContacts((rows) => [contact, ...rows]);
          setListNonce((n) => n + 1);
        }}
      />
      {token && editContact ? (
        <EditContactModal
          key={editContact.id}
          token={token}
          contact={editContact}
          open
          onClose={() => setEditContact(null)}
          onSaved={(updated) => {
            setContacts((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
          }}
        />
      ) : null}
    </div>
  );
}

function useDebounce<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
